import type { Page } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import type ListingsPhoto from "@travily/types/models/ListingsPhoto";
import { useCallback, useRef, useState } from "react";
import { useDirectUploadMultiple } from "@/hooks/use-direct-upload-multiple";

/**
 * The photo endpoints are nested under the listing type, so the caller supplies
 * its own route helpers. Everything else — the optimistic reorder, the modal
 * state and the delete confirmation — is identical for products and services.
 */
export interface PhotoManagerPaths {
  photo: (listingId: string, photoId: string) => string;
  photos: (listingId: string) => string;
  reordering: (listingId: string) => string;
}

interface UsePhotoManagerProps {
  initialPhotos: ListingsPhoto[];
  listingId: string;
  paths: PhotoManagerPaths;
}

function extractPhotos(page: Page): ListingsPhoto[] | null {
  const props = page.props as Record<string, unknown>;
  return Array.isArray(props.photos) ? (props.photos as ListingsPhoto[]) : null;
}

export function usePhotoManager({ initialPhotos, listingId, paths }: UsePhotoManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [photos, setPhotos] = useState(initialPhotos);
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const upload = useDirectUploadMultiple();

  const openModal = useCallback(() => {
    upload.reset();
    setIsModalOpen(true);
  }, [upload]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    upload.reset();
  }, [upload]);

  const confirmUpload = useCallback(() => {
    if (upload.completedSignedIds.length === 0) return;

    router.post(
      paths.photos(listingId),
      { signed_ids: upload.completedSignedIds },
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          closeModal();
          const updated = extractPhotos(page);
          if (updated) setPhotos(updated);
        },
      },
    );
  }, [upload.completedSignedIds, listingId, paths, closeModal]);

  const reorderPhotos = useCallback(
    (orderedIds: string[]) => {
      const before = photosRef.current;
      const reordered = orderedIds.map((id) => before.find((p) => p.id === id)).filter(Boolean) as ListingsPhoto[];
      setPhotos(reordered.map((p, i) => ({ ...p, position: i + 1 })));

      router.patch(
        paths.reordering(listingId),
        { photo_ids: orderedIds },
        {
          preserveState: true,
          preserveScroll: true,
          onError: () => setPhotos(before),
          onSuccess: (page) => {
            const updated = extractPhotos(page);
            if (updated) setPhotos(updated);
          },
        },
      );
    },
    [listingId, paths],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;

    router.delete(paths.photo(listingId, deleteTarget), {
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        setDeleteTarget(null);
        const updated = extractPhotos(page);
        if (updated) setPhotos(updated);
      },
    });
  }, [deleteTarget, listingId, paths]);

  return {
    photos,
    deleteTarget,
    upload,
    isModalOpen,
    openModal,
    closeModal,
    confirmUpload,
    reorderPhotos,
    requestDelete: setDeleteTarget,
    confirmDelete,
    cancelDelete: useCallback(() => setDeleteTarget(null), []),
  };
}
